import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Card, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateroomType, getroomType } from "../../store/slices/roomtype";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EditRoomType() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadRoomType = async () => {
      try {
        const item = { id };
        const res = await dispatch(getroomType(item)).unwrap();
        const room = res.room;
        setForm({
          name: room.name || "",
          maxOccupancy: room.maxOccupancy?.toString() || "",
          basePrice: room.basePrice?.toString() || "",
          extraBedAllowed: room.extraBedAllowed || false,
          extraBedPrice: room.extraBedPrice?.toString() || "",
          maxExtraBeds: room.maxExtraBeds?.toString() || "",
          childBedPrice: room.childwithBedPrice?.toString() || "",
          meals: {
            breakfast: room.meals?.breakfast || {
              price: "",
              isIncluded: false,
            },
            lunch: room.meals?.lunch || { price: "", isIncluded: false },
            dinner: room.meals?.dinner || { price: "", isIncluded: false },
          },
        });
      } catch (err) {
        console.error(err);
      }
    };

    loadRoomType();
  }, [id, dispatch]);

  const MAX_PRICE = 100000;
  const MAX_OCCUPANCY = 10;
  const MAX_EXTRA_BEDS = 10;

  const allowOnlyNumbers = (value) => value.replace(/[^0-9]/g, "");
  const handleLimitedNumberChange = (name, value, maxLimit) => {
    let numericValue = allowOnlyNumbers(value);

    if (Number(numericValue) > maxLimit) {
      numericValue = maxLimit.toString();
      setErrors((prev) => ({
        ...prev,
        [name]: `Maximum allowed value is ${maxLimit}`,
      }));
    } else {
      setErrors((prev) => {
        const copy = { ...prev };
        if (Number(numericValue) <= maxLimit) delete copy[name];
        return copy;
      });
    }

    setForm((prev) => ({
      ...prev,
      [name]: numericValue,
    }));
  };

  const handlePriceChange = (name, value, meal = null) => {
    let numericValue = allowOnlyNumbers(value);

    if (Number(numericValue) > MAX_PRICE) {
      numericValue = MAX_PRICE.toString();

      setErrors((prev) => ({
        ...prev,
        [name]: `Maximum allowed amount is ₹${MAX_PRICE}`,
      }));
    } else {
      setErrors((prev) => {
        const copy = { ...prev };
        if (Number(numericValue) <= MAX_PRICE) delete copy[name];
        return copy;
      });
    }

    if (meal) {
      setForm((prev) => ({
        ...prev,
        meals: {
          ...prev.meals,
          [meal]: {
            ...prev.meals[meal],
            price: numericValue,
          },
        },
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
    }
  };

  const [form, setForm] = useState({
    name: "",
    maxOccupancy: "",
    basePrice: "",
    extraBedAllowed: false,
    extraBedPrice: "",
    maxExtraBeds: "",
    childBedPrice: "",
    meals: {
      breakfast: { price: "", isIncluded: false },
      lunch: { price: "", isIncluded: false },
      dinner: { price: "", isIncluded: false },
    },
  });

  const validateForm = () => {
    let newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Please enter room type";
    }

    if (!form.basePrice) {
      newErrors.basePrice = "Please enter base price";
    } else if (isNaN(form.basePrice) || Number(form.basePrice) <= 0) {
      newErrors.basePrice = "Base price must be a valid amount";
    }
    if (!form.maxOccupancy) {
      newErrors.maxOccupancy = "Please enter max occupancy";
    } else if (
      isNaN(form.maxOccupancy) ||
      Number(form.maxOccupancy) <= 0 ||
      Number(form.maxOccupancy) > MAX_OCCUPANCY
    ) {
      newErrors.maxOccupancy = `Max occupancy must be between 1 and ${MAX_OCCUPANCY}`;
    }

    if (form.extraBedAllowed) {
      if (!form.extraBedPrice || Number(form.extraBedPrice) <= 0) {
        newErrors.extraBedPrice = "Please enter valid extra bed price";
      }

      if (
        !form.maxExtraBeds ||
        isNaN(form.maxExtraBeds) ||
        Number(form.maxExtraBeds) <= 0 ||
        Number(form.maxExtraBeds) > MAX_EXTRA_BEDS
      ) {
        newErrors.maxExtraBeds = `Max extra beds must be between 1 and ${MAX_EXTRA_BEDS}`;
      }
    }

    ["breakfast", "lunch", "dinner"].forEach((meal) => {
      const price = form.meals[meal].price;
      if (price && isNaN(price)) {
        newErrors[meal] = "Meal price must be numeric";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      if (name === "name") {
        const nameRegex = /^[a-zA-Z0-9][\s\S]*$/;

        if (!value.trim()) {
          setErrors((prev) => ({
            ...prev,
            name: "Please enter room type",
          }));
        } else if (!nameRegex.test(value)) {
          setErrors((prev) => ({
            ...prev,
            name: "Please enter letter first",
          }));
        } else if (value.trim().length < 3) {
          setErrors((prev) => ({
            ...prev,
            name: "Minimum 3 characters required",
          }));
        } else if (value.trim().length > 30) {
          setErrors((prev) => ({
            ...prev,
            name: "Maximum 30 characters allowed",
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            name: "",
          }));
        }
      }

      if (name === "extraBedAllowed" && !checked) {
        delete newErrors.extraBedPrice;
        delete newErrors.maxExtraBeds;
      }

      return newErrors;
    });
  };

  const handleMealChange = (meal, field, value) => {
    setForm((prev) => ({
      ...prev,
      meals: {
        ...prev.meals,
        [meal]: {
          ...prev.meals[meal],
          [field]: value,
        },
      },
    }));

    if (errors[meal]) {
      setErrors((prev) => ({ ...prev, [meal]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        id: id,
        name: form.name.trim(),
        maxOccupancy: Number(form.maxOccupancy),
        basePrice: Number(form.basePrice),
        extraBedAllowed: form.extraBedAllowed,
        extraBedPrice: form.extraBedAllowed ? Number(form.extraBedPrice) : 0,
        maxExtraBeds: form.extraBedAllowed ? Number(form.maxExtraBeds) : 0,
        childwithBedPrice: form.extraBedAllowed
          ? Number(form.childBedPrice)
          : 0,
        meals: {
          breakfast: {
            price: Number(form.meals.breakfast.price || 0),
            isIncluded: form.meals.breakfast.isIncluded,
          },
          lunch: {
            price: Number(form.meals.lunch.price || 0),
            isIncluded: form.meals.lunch.isIncluded,
          },
          dinner: {
            price: Number(form.meals.dinner.price || 0),
            isIncluded: form.meals.dinner.isIncluded,
          },
        },
      };

      await dispatch(updateroomType(payload)).unwrap();
      toast.success("Room details edited successfully!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error(err || "Failed to update rooms");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/hotel/home" className="breadcrumb-link active">
            Home
          </Link>
          <span className="breadcrumb-separator">|</span>
          <span className="breadcrumb-link inactive">Add Room Type</span>
        </div>
      </div>

      <Container fluid className="p-4">
        <Card className="p-4 border-0">
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label>Room Type</Form.Label>
                <Form.Control
                  name="name"
                  placeholder="Enter room type"
                  value={form.name}
                  onChange={handleChange}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name}</small>
                )}
              </Col>

              <Col md={4}>
                <Form.Label>Max Occupancy</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter max occupancy"
                  name="maxOccupancy"
                  value={form.maxOccupancy}
                  onChange={(e) =>
                    handleLimitedNumberChange(
                      "maxOccupancy",
                      e.target.value,
                      MAX_OCCUPANCY,
                    )
                  }
                />
                {errors.maxOccupancy && (
                  <small className="text-danger">{errors.maxOccupancy}</small>
                )}
              </Col>

              <Col md={4}>
                <Form.Label>Base Price</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter base price"
                  name="basePrice"
                  value={form.basePrice}
                  onChange={(e) =>
                    handlePriceChange("basePrice", e.target.value)
                  }
                />
                {errors.basePrice && (
                  <small className="text-danger">{errors.basePrice}</small>
                )}
              </Col>
            </Row>

            {/* Extra Bed */}
            <Row className="mb-3">
              <Col md={3}>
                <Form.Check
                  label="Extra Bed Allowed"
                  name="extraBedAllowed"
                  checked={form.extraBedAllowed}
                  onChange={handleChange}
                />
              </Col>

              {form.extraBedAllowed && (
                <>
                  <Col md={3}>
                    <Form.Label>Extra Bed Price</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter extra bed price"
                      value={form.extraBedPrice}
                      onChange={(e) =>
                        handlePriceChange("extraBedPrice", e.target.value)
                      }
                    />
                    {errors.extraBedPrice && (
                      <small className="text-danger">
                        {errors.extraBedPrice}
                      </small>
                    )}
                  </Col>

                  <Col md={3}>
                    <Form.Label>Max Extra Beds</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter max extra beds allowed"
                      value={form.maxExtraBeds}
                      onChange={(e) =>
                        handleLimitedNumberChange(
                          "maxExtraBeds",
                          e.target.value,
                          MAX_EXTRA_BEDS,
                        )
                      }
                    />

                    {errors.maxExtraBeds && (
                      <small className="text-danger">
                        {errors.maxExtraBeds}
                      </small>
                    )}
                  </Col>

                  {/* Extra Child Bed Price */}
                  <Col md={3}>
                    <Form.Label>Child With Bed Price</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter child with bed price"
                      value={form.childBedPrice}
                      onChange={(e) =>
                        handlePriceChange("childBedPrice", e.target.value)
                      }
                    />
                    {errors.childBedPrice && (
                      <small className="text-danger">
                        {errors.childBedPrice}
                      </small>
                    )}
                  </Col>
                </>
              )}
            </Row>

            {/* Meals */}
            <h6 className="mt-4 mb-2">Meals</h6>
            {["breakfast", "lunch", "dinner"].map((meal) => (
              <Row key={meal} className="mb-2 align-items-center">
                <Col md={3} className="text-capitalize">
                  {meal}
                </Col>

                <Col md={3}>
                  <Form.Control
                    type="text"
                    placeholder="Enter price"
                    value={form.meals[meal].price}
                    onChange={(e) =>
                      handlePriceChange(meal, e.target.value, meal)
                    }
                  />
                </Col>

                <Col md={3}>
                  <Form.Check
                    label="Included"
                    checked={form.meals[meal].isIncluded}
                    onChange={(e) =>
                      handleMealChange(meal, "isIncluded", e.target.checked)
                    }
                  />
                </Col>
                {errors[meal] && (
                  <small className="text-danger">{errors[meal]}</small>
                )}
              </Row>
            ))}

            <Row className="mt-4">
              <Col className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner
                        animation="border"
                        size="sm"
                        variant="light"
                        className="me-2"
                      />
                      Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </button>
              </Col>
            </Row>
          </Form>
        </Card>
      </Container>
    </>
  );
}
